import {LightBulbIcon, FileCodeIcon, TerminalIcon, TasklistIcon} from '@primer/octicons-react'
import type {IcebreakerOption} from '../types/icebreaker'

const chronicleIcebreaker: IcebreakerOption = {
  title: 'Improve my workflow',
  subheader: 'Get tips based on my recent sessions',
  icon: <TerminalIcon size={16} className="fgColor-muted" />,
  prompt:
    'Use /chronicle tips to review my session history and recommend personalized tips based on my usage patterns.',
}

const explainRepoIcebreaker: IcebreakerOption = {
  title: 'Explain repository',
  subheader: 'Let the agent help you understand the codebase',
  icon: <LightBulbIcon size={16} className="fgColor-muted" />,
  prompt:
    'Explain this repository. Help me understand the codebase structure, key technologies used, and how the code is organized.',
}

const createPlanIcebreaker: IcebreakerOption = {
  title: 'Create a plan',
  subheader: 'Plan before making changes to the codebase',
  icon: <TasklistIcon size={16} className="fgColor-muted" />,
  prompt:
    'Explore the codebase and create a detailed implementation plan for [INSERT IDEA HERE]. Ask clarifying questions if needed.',
  createPullRequest: false,
}

// Per-variant overrides for the middle icebreaker (slot 1).
// Teams customize this during the experiment workshop.
export const variantMiddleIcebreaker: Record<string, IcebreakerOption> = {
  team_a: {
    title: 'Fix a bug',
    subheader: 'Find and fix an open issue in the codebase',
    icon: <FileCodeIcon size={16} className="fgColor-muted" />,
    prompt:
      'Look at the open issues in this repository, pick one that looks like a bug, and create a fix with tests. Explain your reasoning and the changes you made.',
  },
  team_b: {
    title: 'Add documentation',
    subheader: 'Improve the project documentation and README',
    icon: <FileCodeIcon size={16} className="fgColor-muted" />,
    prompt:
      'Review the existing documentation for this repository. Identify gaps or areas that could be improved, then update the README and add any missing documentation.',
  },
  team_c: {
    title: 'Refactor code',
    subheader: 'Identify and refactor code that could be cleaner',
    icon: <FileCodeIcon size={16} className="fgColor-muted" />,
    prompt:
      'Analyze this codebase for code smells, duplicated logic, or overly complex functions. Refactor the worst offenders while maintaining existing behavior and tests.',
  },
  team_d: {
    title: 'Add a new feature',
    subheader: 'Build a small feature to enhance the project',
    icon: <FileCodeIcon size={16} className="fgColor-muted" />,
    prompt:
      'Explore this repository and suggest a small but useful feature that could be added. Then implement it with proper tests and documentation.',
  },
}

export function getIcebreakers(variant?: string): IcebreakerOption[] {
  const defaults = [explainRepoIcebreaker, chronicleIcebreaker, createPlanIcebreaker]

  if (!variant || variant === 'control' || !(variant in variantMiddleIcebreaker)) {
    return defaults
  }
  const middle = variantMiddleIcebreaker[variant]
  if (middle === undefined) {
    return defaults
  }
  return [explainRepoIcebreaker, middle, createPlanIcebreaker]
}
